import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { FourDots } from "../../Components/FourDots";
import { AuthFooter } from "../../Components/AuthFooter";
import measureLogo from "/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png";
import { COGNITO } from "../../utils/constants";
import { useDispatch } from "react-redux";
import { setToken } from "../../../redux/reducers/authSlice";
import * as Sentry from "@sentry/react";

const TIME_TO_WAIT_BEFORE_RESENDING_CODE = 30000;

const SignUpConfirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //@ts-ignore
  const { email, password }: any = location.state ? location.state : {};
  const [loading, setLoading] = useState(false);
  const [hasResentCode, setHasResentCode] = useState(false);
  const [loadingResendCodeTimer, setLoadingResendCodeTimer] = useState(false);
  const [resendCodeTimerCount, setResendCodeTimerCount] = useState(
    TIME_TO_WAIT_BEFORE_RESENDING_CODE / 1000
  );
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timer;
    let intervalTimer: NodeJS.Timeout;

    if (hasResentCode) {
      setLoadingResendCodeTimer(true);

      intervalTimer = setInterval(() => {
        setResendCodeTimerCount((count) => count - 1);
      }, 1000);

      timer = setTimeout(() => {
        setHasResentCode(false);
        setLoadingResendCodeTimer(false);
        clearInterval(intervalTimer);
        setResendCodeTimerCount(TIME_TO_WAIT_BEFORE_RESENDING_CODE / 1000);
      }, TIME_TO_WAIT_BEFORE_RESENDING_CODE);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      if (intervalTimer) {
        clearInterval(intervalTimer);
      }
    };
  }, [hasResentCode]);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = pastedData.split('').slice(0, 6);
    
    setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(newOtp.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const code = otp.join('');
    if (code.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    try {
      setLoading(true);
      // get confirm response
      const confirmResponse = await Auth.confirmSignUp(
        email,
        code.trim()
      );

      if (confirmResponse === COGNITO.CONFIRM_SIGNUP_SUCCESS_RESPONSE) {
        if (location.state) {
          const user = await Auth.signIn(email, password);

          const token = user.signInUserSession.idToken.jwtToken;

          dispatch(setToken({ token }));

          if (token) {
            setLoading(false);
            // Don't navigate - let App.tsx handle route switching based on isAuthenticated
            // This ensures profile is loaded before showing dashboard
          }
        }
      }
    } catch (error: any) {
      Sentry.captureException(error);
      setLoading(false);
      if (error.code === "CodeMismatchException") {
        toast.error("Invalid code. Please try again");
      } else if (error.code === "ExpiredCodeException") {
        toast.error("The code has expired. Please try again");
      } else {
        toast.error("Something unexpected happened");
      }
    }
  };

  const handleResendCode = async () => {
    try {
      await Auth.resendSignUp(email);
      setHasResentCode(true);
      toast.success("Verification code sent to " + email);
    } catch (e: any) {
      Sentry.captureException(e);
      setHasResentCode(false);
      toast.error(
        "Something unexpected happened while resending your code. Try again later"
      );
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <Link to="/" className="mb-8">
              <img 
                src={measureLogo} 
                alt="Measure Ignite" 
                className="h-25 w-auto"
              />
            </Link>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-semibold mb-2">Verify your email</h1>
            <p className="text-gray-600">
              We sent a 6-digit code to <span className="font-semibold text-gray-900">{email}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Enter verification code
              </label>
              <div className="flex gap-2 justify-between" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition"
                  />
                ))}
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={otp.join('').length !== 6 || loading}
            >
              {loading ? "Creating account..." : "Create account"}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loadingResendCodeTimer}
                className="text-sm font-semibold text-orange-600 hover:text-orange-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {loadingResendCodeTimer ? (
                  `Code sent! You have to wait ${resendCodeTimerCount} seconds before sending another one`
                ) : (
                  "Didn't receive a code? Click here to resend"
                )}
              </button>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-orange-600 flex items-center justify-center gap-1"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Back to sign in
              </Link>
            </div>
          </form>

          <AuthFooter />
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-center items-center">
          <div className="max-w-lg text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              Almost there!
            </h2>
            <p className="text-gray-700 text-lg mb-8">
              Just one more step to join your community and start tracking your impact.
            </p>
            <FourDots size="2xl" opacity={0.7} />
          </div>
          
          <div className="space-y-4 text-center">
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                <span className="text-orange-500 font-semibold">✓</span>
              </div>
              <span>Secure verification</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                <span className="text-orange-500 font-semibold">✓</span>
              </div>
              <span>Your data is protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpConfirm;
